import React, { useState } from "react";
import {
  MdClose,
  MdPerson,
  MdEmail,
  MdWarning,
  MdCheckCircle,
} from "react-icons/md";
import "./Navbar.css";
import errorFunctions from "@store/errorFunctions";
import config from "@config";
import utilityFunctions from "@store/utilityFunctions";

const LoginAsUser = ({ handleCloseModal, protectedFetch, userInfo }) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateInput = () => {
    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      setError("Please enter an email address");
      return false;
    }

    if (!isValidEmail(trimmedEmail)) {
      setError("Please enter a valid email address");
      return false;
    }

    return true;
  };

  const getCustomerPortalUrl = (baseUrl) => {
    // Transform URL for branch deployment (e.g., https://dreame-rmas-customer -> https://develop--dreame-rmas-customer)
    if (baseUrl && baseUrl.startsWith("https://")) {
      return baseUrl.replace("https://", "https://develop--");
    }
    return baseUrl;
  };

  const removeTrailingSlash = (url) => {
    if (!url) {
      return url;
    }
    return url.endsWith("/") ? url.slice(0, -1) : url;
  };

  const updateUserInfo = (tokens, userData) => {
    const userInfoNew = {
      admin_userInfo: userInfo,
      ...userInfo,
      access_token: tokens.access,
      refresh_token: tokens.refresh,
      admin_token: userInfo.access_token,
      admin_refresh_token: userInfo.refresh_token,
      is_login_as: true,
      login_as_user: userData.name,
      login_as_email: email.trim(),
      role: userData.role,
      user: {
        ...userData,
      },
    };

    // For Customer role, redirect to customer portal
    if (userInfoNew.role === "Customer") {
      alert(
        "You have successfully logged in as a customer and will be redirected to the customer portal.",
      );
      const customerPortalUrl = config.baseUrl.includes("develop")
        ? getCustomerPortalUrl(config.customerUrl)
        : config.customerUrl;

      const userInfoForCustomerPortal = {
        access_token: userInfoNew.access_token,
        refresh_token: userInfoNew.refresh_token,
        name: userInfoNew.login_as_user,
        email: userInfoNew.login_as_email,
        role: userInfoNew.role,
        user: {
          role: userInfoNew.role,
          id: userInfoNew.user.id,
        },
      };

      const customerPortalUrlNormalized =
        removeTrailingSlash(customerPortalUrl);
      window.open(
        customerPortalUrlNormalized +
          "/" +
          JSON.stringify(userInfoForCustomerPortal),
        "_blank",
      );
    } else {
      // Mirror App.js's userHasAuthenticated: pick the new user's first
      // brand matching this deployment's country so ticket/brand-scoped
      // fetches don't go out with an empty brandId after the reload.
      const firstBrandId =
        (userData?.brands || []).filter(
          (brand) => brand.country === config.country,
        )?.[0]?.id || "";
      utilityFunctions.setCookie("brandId", firstBrandId);
      utilityFunctions.setCookie("brandIds", "");

      localStorage.setItem("userInfo", JSON.stringify(userInfoNew));

      if (userInfoNew.role.includes("Commercial")) {
        window.location.href = "/commercial/tickets";
      } else {
        window.location.reload();
      }
    }

    handleCloseModal();
  };

  const handleLogin = async () => {
    if (!validateInput()) {
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await protectedFetch(
        `${config.backendServer}login-as/`,
        "POST",
        { email: email.trim() },
        errorFunctions.checkResponseThenJson,
      );

      if (response.tokens && userInfo) {
        updateUserInfo(response.tokens, response.user);
      } else {
        setError("Invalid response from server. Please try again.");
      }
    } catch (err) {
      setError(err.message || "Failed to login as user. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const restoreAdminSession = () => {
    const userInfoNew = {
      ...userInfo,
      ...userInfo.admin_userInfo,
      access_token: userInfo.admin_token,
      refresh_token: userInfo.admin_refresh_token,
    };

    delete userInfoNew.admin_token;
    delete userInfoNew.admin_refresh_token;
    delete userInfoNew.login_as_user;
    delete userInfoNew.login_as_email;
    delete userInfoNew.is_login_as;
    delete userInfoNew.admin_userInfo;

    localStorage.setItem("userInfo", JSON.stringify(userInfoNew));

    // The brandId/brandIds cookies were pointed at the impersonated user's
    // brand during login-as; reset them to the admin's own first brand so
    // the ticket list isn't left scoped to a brand the admin never picked.
    const firstBrandId =
      (userInfoNew?.user?.brands || []).filter(
        (brand) => brand.country === config.country,
      )?.[0]?.id || "";
    utilityFunctions.setCookie("brandId", firstBrandId);
    utilityFunctions.setCookie("brandIds", "");
  };

  const handleLogout = () => {
    setLoading(true);
    restoreAdminSession();
    setLoading(false);
    handleCloseModal();
    window.location.href = "/tickets";
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (error) {
      setError("");
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !loading) {
      handleLogin();
    }
  };

  const renderLoginAsStatus = () => (
    <div className="login-as-status-container">
      <div className="login-as-status-icon">
        <MdCheckCircle size={48} color="#28a745" />
      </div>
      <div className="login-as-status-content">
        <p className="login-as-status-label">Currently logged in as</p>
        <div className="login-as-user-info">
          <MdPerson size={18} />
          <span className="login-as-user-name">{userInfo.login_as_user}</span>
        </div>
        <div className="login-as-user-email">
          <MdEmail size={16} />
          <span>{userInfo.login_as_email}</span>
        </div>
      </div>
    </div>
  );

  const renderLoginForm = () => (
    <div className="login-as-form-container">
      <p className="login-as-description">
        Enter the email address of the user you want to login as. This will
        allow you to view and manage their account.
      </p>
      <div className="login-as-input-group">
        <label className="login-as-label" htmlFor="userEmail">
          Email Address <span className="required-asterisk">*</span>
        </label>
        <div className="login-as-input-wrapper">
          <MdEmail className="login-as-input-icon" size={20} />
          <input
            type="email"
            id="userEmail"
            className={`login-as-input ${error ? "login-as-input-error" : ""}`}
            value={email}
            onChange={handleEmailChange}
            onKeyPress={handleKeyPress}
            placeholder="user@example.com"
            disabled={loading}
            autoFocus
          />
        </div>
        {error && (
          <div className="login-as-error">
            <MdWarning size={16} />
            <span>{error}</span>
          </div>
        )}
      </div>
    </div>
  );

  const renderFooterButtons = () => {
    if (userInfo.is_login_as) {
      return (
        <div className="login-as-footer-buttons">
          <button
            className="login-as-btn login-as-btn-logout"
            onClick={handleLogout}
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="login-as-spinner"></span>
                Logging out...
              </>
            ) : (
              <>Logout and Return to Admin</>
            )}
          </button>
        </div>
      );
    }

    return (
      <div className="login-as-footer-buttons">
        <button
          className="login-as-btn login-as-btn-secondary"
          onClick={handleCloseModal}
          disabled={loading}
        >
          Cancel
        </button>
        <button
          className="login-as-btn login-as-btn-primary"
          onClick={handleLogin}
          disabled={loading || !email.trim()}
        >
          {loading ? (
            <>
              <span className="login-as-spinner"></span>
              Logging in...
            </>
          ) : (
            <>Login as User</>
          )}
        </button>
      </div>
    );
  };

  return (
    <div className="login-as-modal-container">
      <div className="login-as-card">
        <button
          onClick={handleCloseModal}
          className="login-as-close-btn"
          aria-label="Close"
        >
          <MdClose size={24} />
        </button>

        <div className="login-as-header">
          <div className="login-as-header-icon">
            <MdPerson size={32} />
          </div>
          <h2 className="login-as-title">
            {userInfo.is_login_as ? "Login Status" : "Login as User"}
          </h2>
          <p className="login-as-subtitle">
            {userInfo.is_login_as
              ? "You are currently impersonating another user"
              : "Access another user's account for support purposes"}
          </p>
        </div>

        <div className="login-as-body">
          {userInfo.is_login_as ? renderLoginAsStatus() : renderLoginForm()}
        </div>

        <div className="login-as-footer">{renderFooterButtons()}</div>
      </div>
    </div>
  );
};

export default LoginAsUser;
